// src/contexts/token/TokenDerivativesProvider.tsx
/**
 * TokenDerivativesProvider - Centralizes derivatives data fetching
 * 
 * Fetches and provides:
 * - Open Interest history
 * - OI-weighted funding rates
 * - Volume-weighted funding rates
 * - Per-exchange funding rates
 * 
 * All calculations use lib/metrics functions.
 */

import React, { createContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  openInterestHistoryQueryOptions,
  weightedFundingRateHistoryQueryOptions,
  fundingRateHistoryQueryOptions,
  tokenPriceHistoryQueryOptions,
} from "@/services/queries/coinglass/coinglassQueries";
import { EXCHANGES } from "@/constants/coinglass/coinglass";
import { dateIntToTimestamp } from "@/utils/coinglass";
import {
  computeFundingStats,
  oiChangePct,
  determineRegime,
  findFundingExtremes,
  sliceFundingByWindow,
} from "@/lib/metrics";
import type {
  OpenInterestHistoryResponse,
  WeightedFundingRateHistoryResponse,
  FundingRateHistoryResponse,
  TokenHistoryResponse,
} from "@/types/coinglass";
import type {
  TokenDerivativesContextValue,
  FundingRatePoint,
  OpenInterestPoint,
} from "./types";

// ============================================================================
// CONTEXT
// ============================================================================

const TokenDerivativesContext = createContext<TokenDerivativesContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface TokenDerivativesProviderProps {
  ticker: string;
  children: React.ReactNode;
}

export const TokenDerivativesProvider: React.FC<TokenDerivativesProviderProps> = ({
  ticker,
  children,
}) => {
  const tkr = ticker.toUpperCase();
  
  // Fetch all derivatives data
  const oiQuery = useQuery({
    ...openInterestHistoryQueryOptions({ ticker: tkr }),
    enabled: !!tkr,
  });
  
  const oiwQuery = useQuery({
    ...weightedFundingRateHistoryQueryOptions({ ticker: tkr, dataType: 'oiw' }),
    enabled: !!tkr,
  });
  
  const volwQuery = useQuery({
    ...weightedFundingRateHistoryQueryOptions({ ticker: tkr, dataType: 'volw' }),
    enabled: !!tkr,
  });
  
  // Fetch per-exchange funding rates
  const exchangeFrQuery = useQuery({
    ...fundingRateHistoryQueryOptions({ ticker: tkr, exchanges: [...EXCHANGES] }),
    staleTime: 60_000,
    enabled: !!tkr,
  });
  
  // Fetch price data for chart overlays
  const priceQuery = useQuery({
    ...tokenPriceHistoryQueryOptions({ ticker: tkr }),
    enabled: !!tkr,
  });
  
  const value = useMemo<TokenDerivativesContextValue>(() => {
    // Build price lookup map from price history
    const priceMap = new Map<number, number | null>();
    const priceRaw = priceQuery.data as TokenHistoryResponse | undefined;
    const priceMetric = priceRaw?.metrics?.find(
      (m: { metric_key: string }) => m.metric_key === 'price_close'
    );
    if (priceMetric) {
      priceMetric.dates.forEach((dateInt: number, idx: number) => {
        const ts = dateIntToTimestamp(dateInt);
        priceMap.set(ts, priceMetric.values[idx] ?? null);
      });
    }
    
    // Process Open Interest with price overlay
    const oiRaw = oiQuery.data as OpenInterestHistoryResponse | undefined;
    const oiSeries: OpenInterestPoint[] = oiRaw?.series?.[tkr]?.map(([dateInt, openInterest]) => {
      const ts = dateIntToTimestamp(dateInt);
      return {
        timestamp: ts,
        openInterest,
        price: priceMap.get(ts) ?? null,
      };
    }) || [];
    
    const latestOi = oiSeries.length > 0 
      ? oiSeries[oiSeries.length - 1]!.openInterest 
      : null;
    
    // Extract OI values for change calculations
    const oiValues = oiSeries.map(p => p.openInterest);
    
    // Process OI-weighted funding with price overlay
    const oiwRaw = oiwQuery.data as WeightedFundingRateHistoryResponse | undefined;
    const oiwSeries: FundingRatePoint[] = oiwRaw?.series?.[tkr]?.map(([dateInt, fundingRate]) => {
      const ts = dateIntToTimestamp(dateInt);
      return {
        timestamp: ts,
        rate: fundingRate,
        price: priceMap.get(ts) ?? null,
      };
    }) || [];
    
    const latestOiw = oiwSeries.length > 0 
      ? oiwSeries[oiwSeries.length - 1]!.rate 
      : null;
    
    // Compute stats for multiple windows using lib/metrics
    const oiwRates7d = sliceFundingByWindow(oiwSeries, 7).map(p => p.rate);
    const oiwRates30d = sliceFundingByWindow(oiwSeries, 30).map(p => p.rate);
    const oiwRates180d = sliceFundingByWindow(oiwSeries, 180).map(p => p.rate);
    const oiwRates365d = sliceFundingByWindow(oiwSeries, 365).map(p => p.rate);
    
    const oiw7d = computeFundingStats(oiwRates7d);
    const oiw30d = computeFundingStats(oiwRates30d);
    const oiw180d = computeFundingStats(oiwRates180d);
    const oiw365d = computeFundingStats(oiwRates365d);
    
    // Compute extremes using lib/metrics
    const extremes7d = findFundingExtremes(sliceFundingByWindow(oiwSeries, 7));
    const extremes30d = findFundingExtremes(sliceFundingByWindow(oiwSeries, 30));
    const extremes180d = findFundingExtremes(sliceFundingByWindow(oiwSeries, 180));
    const extremes365d = findFundingExtremes(sliceFundingByWindow(oiwSeries, 365));
    
    // Process Volume-weighted funding with price overlay
    const volwRaw = volwQuery.data as WeightedFundingRateHistoryResponse | undefined;
    const volwSeries: FundingRatePoint[] = volwRaw?.series?.[tkr]?.map(([dateInt, fundingRate]) => {
      const ts = dateIntToTimestamp(dateInt);
      return {
        timestamp: ts,
        rate: fundingRate,
        price: priceMap.get(ts) ?? null,
      };
    }) || [];
    
    const latestVolw = volwSeries.length > 0 
      ? volwSeries[volwSeries.length - 1]!.rate 
      : null;
    
    const volwRates7d = sliceFundingByWindow(volwSeries, 7).map(p => p.rate);
    const volwRates30d = sliceFundingByWindow(volwSeries, 30).map(p => p.rate);
    const volwRates180d = sliceFundingByWindow(volwSeries, 180).map(p => p.rate);
    const volwRates365d = sliceFundingByWindow(volwSeries, 365).map(p => p.rate);
    
    const volw7d = computeFundingStats(volwRates7d);
    const volw30d = computeFundingStats(volwRates30d);
    const volw180d = computeFundingStats(volwRates180d);
    const volw365d = computeFundingStats(volwRates365d);
    
    // Regime detection using lib/metrics
    const oiChange7dPct = oiChangePct(oiValues, 7);
    const regime = determineRegime(oiChange7dPct, oiw7d.cumulative);
    
    // Process per-exchange funding rates
    const exchangeFrRaw = exchangeFrQuery.data as FundingRateHistoryResponse | undefined;
    const exchangeRates: Array<{ exchange: string; rate: number }> = [];
    const symbolData = exchangeFrRaw?.series?.[tkr] as Record<string, Array<[number, number]>> | undefined;
    if (symbolData) {
      Object.entries(symbolData).forEach(([exchange, arr]) => {
        const last = arr && arr.length ? arr[arr.length - 1] : null;
        if (last) {
          const rate = last[1];
          if (typeof rate === 'number' && Number.isFinite(rate)) {
            exchangeRates.push({ exchange, rate });
          }
        }
      });
      exchangeRates.sort((a, b) => a.exchange.localeCompare(b.exchange));
    }
    
    // Check if we have any meaningful data
    const hasData = oiSeries.length > 0 || oiwSeries.length > 0 || volwSeries.length > 0;
    
    const isLoading = oiQuery.isLoading || oiwQuery.isLoading || volwQuery.isLoading || exchangeFrQuery.isLoading || priceQuery.isLoading;
    const isError = oiQuery.isError || oiwQuery.isError || volwQuery.isError || exchangeFrQuery.isError || priceQuery.isError;
    const error = oiQuery.error || oiwQuery.error || volwQuery.error || exchangeFrQuery.error || priceQuery.error;
    
    return {
      ticker: tkr,
      
      openInterest: {
        series: oiSeries,
        latest: latestOi,
        change1dPct: oiChangePct(oiValues, 1),
        change7dPct: oiChange7dPct,
        change30dPct: oiChangePct(oiValues, 30),
        change365dPct: oiChangePct(oiValues, 365),
      },
      
      fundingOiWeighted: {
        series: oiwSeries,
        latest: latestOiw,
        stats: {
          '7d': oiw7d,
          '30d': oiw30d,
          '180d': oiw180d,
          '365d': oiw365d,
        },
        extremes: {
          '7d': extremes7d,
          '30d': extremes30d,
          '180d': extremes180d,
          '365d': extremes365d,
        },
      },
      
      fundingVolWeighted: {
        series: volwSeries,
        latest: latestVolw,
        stats: {
          '7d': volw7d,
          '30d': volw30d,
          '180d': volw180d,
          '365d': volw365d,
        },
      },
      
      exchangeRates,
      regime,
      hasData,
      isLoading,
      isError,
      error: error as Error | null,
      
      refetch: () => {
        oiQuery.refetch();
        oiwQuery.refetch();
        volwQuery.refetch();
        exchangeFrQuery.refetch();
        priceQuery.refetch();
      },
    };
  }, [
    tkr,
    oiQuery,
    oiwQuery,
    volwQuery,
    exchangeFrQuery,
    priceQuery,
  ]);
  
  return (
    <TokenDerivativesContext.Provider value={value}>
      {children}
    </TokenDerivativesContext.Provider>
  );
};

export { TokenDerivativesContext };
